import { AlertTriangle, Clock, Inbox, Loader2, ShieldOff } from 'lucide-react'
import type * as React from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * Estados padrão de tela (molde do Hub SISTEMAS): todo componente interativo
 * cobre carregando, vazio, erro e sem-permissão. Centralizar evita cada tela
 * inventar o seu.
 */

function Frame({
  icon,
  title,
  description,
  action,
  tone = 'muted',
  className,
}: {
  icon: React.ReactNode
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
  tone?: 'muted' | 'destructive'
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border px-6 py-10 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-md',
          tone === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground',
        )}
        aria-hidden
      >
        {icon}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function Loading({ label = 'Carregando...' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[200px] items-center justify-center gap-2 text-sm text-muted-foreground"
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </div>
  )
}

/** Esqueleto de lista, para cargas dentro de páginas já montadas. */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2" role="status" aria-label="Carregando">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  )
}

export function Empty({
  title = 'Nada por aqui ainda',
  description,
  action,
  className,
}: {
  title?: string
  description?: React.ReactNode
  action?: React.ReactNode
  className?: string
}) {
  return (
    <Frame icon={<Inbox className="h-5 w-5" />} title={title} description={description} action={action} className={className} />
  )
}

export function ErrorState({
  title = 'Algo deu errado',
  description = 'Não foi possível carregar esta tela.',
  onRetry,
}: {
  title?: string
  description?: React.ReactNode
  onRetry?: () => void
}) {
  return (
    <Frame
      tone="destructive"
      icon={<AlertTriangle className="h-5 w-5" />}
      title={title}
      description={description}
      action={
        onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Tentar novamente
          </Button>
        ) : undefined
      }
    />
  )
}

export function Forbidden({
  description = 'Você não tem permissão para acessar este conteúdo. Fale com o gestor do Supply.',
}: {
  description?: React.ReactNode
}) {
  return <Frame icon={<ShieldOff className="h-5 w-5" />} title="Acesso restrito" description={description} />
}

/** Entrou, mas falta alguém liberar (ex.: ficha do SSO sem papel nem cargo). */
export function Pending({ title = 'Acesso pendente', description }: { title?: string; description?: React.ReactNode }) {
  return <Frame icon={<Clock className="h-5 w-5" />} title={title} description={description} />
}

export function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">{children}</div>
    </div>
  )
}

/** Carregando / erro / conteúdo, para uma `useLoad`. */
export function LoadGate<T>({
  state,
  children,
  skeleton,
}: {
  state: { data: T | undefined; error: string | null; loading: boolean; reload: () => void }
  children: (data: T) => React.ReactNode
  skeleton?: React.ReactNode
}) {
  if (state.error && state.data === undefined) return <ErrorState description={state.error} onRetry={state.reload} />
  if (state.data === undefined) return <>{skeleton ?? <ListSkeleton />}</>
  return <>{children(state.data)}</>
}
